import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, TextInput, Platform, ScrollView, StatusBar, Keyboard } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { profileStrings as strings } from '@/constants/residents/strings';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { launchImageLibrary, ImagePickerResponse } from 'react-native-image-picker';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { setUser } from '@/store/slices/authSlice';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { showAlert } from '@/utils/common/alert';
import { GradientBackground, GlassCard } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';

const HP = REDESIGN_SPACING.horizontalPadding;

export const ProfileBioScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const { user } = useAppSelector((s) => s.auth);
  const insets = useSafeAreaInsets();

  const displayName = useMemo(() => {
    if (!user) return '';
    if (user.fullName && user.fullName.trim().length > 0) return user.fullName;
    const composed = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return composed || user.email;
  }, [user]);

  const initialBio = (user as any)?.roommateBio || '';
  const [bio, setBio] = useState<string>(initialBio);
  const [isSaving, setIsSaving] = useState(false);
  const [localPhotoUri, setLocalPhotoUri] = useState<string | null>(null);

  const scrollViewRef = useRef<ScrollView>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const handleBioFocus = useCallback(() => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 350);
  }, []);

  const photoSource = useMemo(() => {
    if (localPhotoUri) return { uri: localPhotoUri } as const;
    if (user?.photoUrl || user?.profilePictureUrl) {
      return { uri: (user?.photoUrl || user?.profilePictureUrl) as string } as const;
    }
    return null;
  }, [localPhotoUri, user?.photoUrl, user?.profilePictureUrl]);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onPickPhoto = useCallback(() => {
    const options = {
      mediaType: 'photo' as const,
      quality: 0.9 as const,
      selectionLimit: 1,
      includeBase64: true,
      maxWidth: 150,
      maxHeight: 150,
    };
    launchImageLibrary(options, async (response: ImagePickerResponse) => {
      if (response.didCancel || response.errorCode) {
        return;
      }
      const asset = response.assets && response.assets[0];
      if (!asset) return;
      if (asset.uri) {
        setLocalPhotoUri(asset.uri);
      }
      try {
        if (!user?.id) {
          throw new Error('Missing user id');
        }
        if (asset.base64) {
          const updated = await AuthAPI.updateProfileImage(user.id, asset.base64);
          dispatch(setUser(updated as any));
          showAlert('Success', 'Profile photo updated');
        }
      } catch (e: any) {
        const msg = e?.message || 'Failed to update photo';
        showAlert('Error', msg);
      }
    });
  }, [dispatch, user?.id]);

  const onSave = useCallback(async () => {
    try {
      setIsSaving(true);
      if (!user?.id) {
        throw new Error('Missing user id');
      }
      const updated = await AuthAPI.updateBio(user.id, bio);
      dispatch(setUser(updated as any));
      showAlert('Success', 'Saved');
    } catch (e: any) {
      const msg = e?.message || 'Failed to save';
      showAlert('Error', msg);
    } finally {
      setIsSaving(false);
    }
  }, [bio, dispatch]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="ProfileBio"
        darkBackground
        headerVariant="gradient"
      >
        <KeyboardDismissWrapper style={styles.flex}>
          <ScrollView
            ref={scrollViewRef}
            style={styles.flex}
            contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 24 + keyboardHeight }]}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            showsVerticalScrollIndicator={false}
          >
              <BackLink
                label="Back to settings"
                onPress={handleBack}
                color={PRIMITIVE_COLORS.resioBlue}
              />

              <GlassCard borderRadius={20} padding={24} style={styles.avatarCard}>
                <View style={styles.avatarBlock}>
                  <View style={[styles.avatarContainer, { borderColor: colors.card.border, backgroundColor: colors.glass.buttonBg }]}>
                    {photoSource ? (
                      <Image source={photoSource} style={styles.avatar} />
                    ) : (
                      <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={56} height={56} />
                    )}
                    <TouchableOpacity style={[styles.editButton, { backgroundColor: colors.card.bg, borderColor: colors.card.border }]} onPress={onPickPhoto} accessibilityLabel="Edit Profile Photo">
                      <Icon name="pencil" size={16} color={colors.text.primary} />
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.name, { color: colors.text.primary }]}>{displayName}</Text>
                  {!!user?.email && <Text style={[styles.sub, { color: colors.text.dim }]}>{user.email}</Text>}
                </View>
              </GlassCard>

              <GlassCard borderRadius={20} padding={24} style={styles.bioCard}>
                <Text style={[styles.bioLabel, { color: colors.text.secondary }]}>Bio</Text>
                <TextInput
                  value={bio}
                  onChangeText={setBio}
                  placeholder={strings.bio.placeholder}
                  placeholderTextColor={colors.text.dimmer}
                  multiline
                  onFocus={handleBioFocus}
                  style={[styles.bioInput, { borderColor: colors.card.border, color: colors.text.primary, backgroundColor: colors.glass.buttonBg }]}
                />
              </GlassCard>

              <TouchableOpacity
                style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                onPress={onSave}
                disabled={isSaving}
                accessibilityRole="button"
              >
                <Text style={styles.saveButtonText}>{isSaving ? 'Saving...' : strings.bio.button.save}</Text>
              </TouchableOpacity>
          </ScrollView>
        </KeyboardDismissWrapper>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
    paddingTop: 8,
    gap: 16,
  },
  avatarCard: {
    marginTop: 8,
  },
  avatarBlock: {
    alignItems: 'center',
  },
  avatarContainer: {
    height: 100,
    width: 100,
    borderRadius: 50,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  avatar: {
    height: 98,
    width: 98,
    borderRadius: 49,
  },
  name: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
    marginTop: 12,
    textAlign: 'center',
  },
  sub: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontWeight: '400',
    marginTop: 4,
    textAlign: 'center',
  },
  editButton: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    height: 36,
    width: 36,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bioCard: {
    gap: 12,
  },
  bioLabel: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  bioInput: {
    minHeight: 180,
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    textAlignVertical: 'top',
    fontSize: 15,
  },
  saveButton: {
    alignItems: 'center',
    paddingVertical: 14,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: REDESIGN_SPACING.buttonRadius,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
});

export default ProfileBioScreen;
